export interface FunnelStage {
  key: string;
  label: string;
  /** Formatted value; `—` for a blank. */
  valueText: string;
  /** Bar width, 0…100, relative to the largest stage. */
  widthPct: number;
  /** `% of previous` text (`40.0% of previous`), or `null` for the first stage / no comparison. */
  conversionText: string | null;
}

export interface FunnelBarsProps {
  stages: readonly FunnelStage[];
  'aria-label': string;
}

/**
 * A horizontal funnel (plan §2.1): one hue (series slot 1) on a muted track, the value in its own column (never on the fill), the stage
 * conversion beside it. Magnitude is one series, so one colour; a blank stage has no bar and reads `—`.
 */
export const FunnelBars = ({ stages, ...rest }: FunnelBarsProps) => (
  <ol
    className="space-y-1.5"
    aria-label={rest['aria-label']}
    data-funnel
  >
    {stages.map((stage) => (
      <li
        key={stage.key}
        className="grid grid-cols-[9rem_minmax(0,1fr)_6rem_8.5rem] items-center gap-2 text-xs"
        data-funnel-stage={stage.key}
        title={`${stage.label}: ${stage.valueText}${stage.conversionText ? ` · ${stage.conversionText}` : ''}`}
      >
        <span className="truncate text-muted-foreground">{stage.label}</span>
        <span className="relative h-5 rounded-sm bg-muted">
          <span
            className="absolute inset-y-0 start-0 rounded-e-[4px] rounded-s-sm"
            style={{ width: `${stage.widthPct}%`, background: 'var(--series-1)' }}
          />
        </span>
        <span className="text-end font-mono font-semibold tabular-nums text-foreground">{stage.valueText}</span>
        <span
          className="text-end font-mono tabular-nums text-muted-foreground"
          data-conversion
        >
          {stage.conversionText ?? ''}
        </span>
      </li>
    ))}
  </ol>
);
